import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, unlockPatterns } from "../lib/api";
import { OFFLINE_FLAG, bundleNow, clearBundle, clearOfflineJobs, loadBundle, offlineEnabled, saveBundle, takeOfflineJobs } from "../lib/offlineStore";

const SYNC_EVERY = 30 * 60 * 1000;

export const syncOffline = async (silent = true) => {
  const b = await api.offlineBundle();
  for (const v of b.vehicles || []) await unlockPatterns(v.patterns);  // geometry travels locked; the laptop stores it ready to cut
  await saveBundle(b);
  if (!silent) toast.success(`Offline copy updated · ${b.vehicles.length} vehicle${b.vehicles.length === 1 ? "" : "s"} saved on this computer`);
  return b;
};

// Keeps the laptop's offline copy fresh for customers with Offline mode on; does nothing for anyone else.
export const useOfflineSync = (user) => {
  const enabled = offlineEnabled(user) && !user?.offline_session;
  const timer = useRef(null);
  useEffect(() => { loadBundle(); }, []);
  useEffect(() => {
    if (!user) return undefined;
    if (!enabled) {
      if (!user.offline_session && localStorage.getItem(OFFLINE_FLAG)) {
        // switched off by the owner (or a different account signed in): wipe the local copy + shell cache
        localStorage.removeItem(OFFLINE_FLAG);
        clearBundle();
        navigator.serviceWorker?.getRegistrations?.().then((rs) => rs.forEach((r) => r.unregister()));
      }
      return undefined;
    }
    localStorage.setItem(OFFLINE_FLAG, "1");
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").then((reg) => { reg.update?.().catch(() => {}); setTimeout(() => (reg.active || navigator.serviceWorker.controller)?.postMessage("xpattern:precache"), 4000); }).catch(() => {});
    const run = async () => {
      if (navigator.onLine === false || document.visibilityState !== "visible") return;
      try {
        const jobs = await takeOfflineJobs();
        if (jobs.length) { await api.offlineJobsUpload(jobs); await clearOfflineJobs(); }
        const cur = bundleNow();
        if (!cur || Date.now() - new Date(cur.synced_at).getTime() > SYNC_EVERY) await syncOffline();
      } catch { /* try again on the next tick */ }
    };
    run();
    timer.current = setInterval(run, 5 * 60 * 1000);
    const onOpen = () => { clearTimeout(timer.open); timer.open = setTimeout(() => syncOffline().catch(() => {}), 45000); };
    window.addEventListener("xpattern:vehicle-opened", onOpen);
    window.addEventListener("online", run);
    return () => { clearInterval(timer.current); clearTimeout(timer.open); window.removeEventListener("xpattern:vehicle-opened", onOpen); window.removeEventListener("online", run); };
  }, [user?.id, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
};
